/**
 * Onboarding Service
 * Frontend service for interacting with product onboarding wizard API
 */

import { authenticatedFetch, API_BASE_URL } from '../config/api';

export interface AiModel {
  id: string;
  name: string;
  gender: 'male' | 'female' | 'unisex';
  bodyType: string;
  imageUrl: string;
  thumbnailUrl?: string;
  description?: string;
}

export interface ShowcaseProduct {
  id: string;
  product: any;
  aiModel: any;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  imageUrl?: string;
  videoUrl?: string;
  error?: string;
}

export interface PreviewItem {
  id: string;
  productId: string;
  productName: string;
  productImage: string;
  productSku: string;
  aiModelId: string;
  aiModelName: string;
  aiModelGender: string;
  aiModelImage: string;
  generatedImageUrl?: string;
  generatedVideoUrl?: string;
  generationStatus: 'pending' | 'processing' | 'completed' | 'failed';
  generationError?: string;
}

export interface PreviewStatus {
  items: PreviewItem[];
  stats: {
    total: number;
    pending: number;
    processing: number;
    completed: number;
    failed: number;
  };
  isComplete: boolean;
  hasErrors: boolean;
}

export interface VideoForReview {
  id: string;
  productId: string;
  productName: string;
  productImage: string;
  productSku: string;
  aiModelId: string;
  aiModelName: string;
  aiModelGender: string;
  aiModelImage: string;
  generatedImageUrl?: string;
  generatedVideoUrl?: string;
  generationStatus: 'pending' | 'processing' | 'completed' | 'failed';
  generationError?: string;
  approvalStatus: 'pending_review' | 'approved' | 'rejected';
  rejectionReason?: string;
  regenerationCount: number;
  displayOrder: number;
  isActive?: boolean;
}

export interface VideoReviewStats {
  total: number;
  pending: number;
  completed: number;
  failed: number;
  approved: number;
  rejected: number;
  pendingReview: number;
  active: number;
  inactive: number;
}

export class OnboardingService {
  /**
   * Get available AI models, optionally filtered by gender
   */
  static async getAiModels(gender?: 'male' | 'female' | 'unisex'): Promise<AiModel[]> {
    try {
      const url = gender
        ? `${API_BASE_URL}/onboarding/ai-models?gender=${gender}`
        : `${API_BASE_URL}/onboarding/ai-models`;

      const response = await authenticatedFetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data.models;
    } catch (error) {
      console.error('Failed to fetch AI models:', error);
      throw error;
    }
  }

  /**
   * Get showcase products and their generation status
   */
  static async getShowcaseStatus(brandId: string, productCode: string): Promise<ShowcaseProduct[]> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/onboarding/${brandId}/${productCode}/showcase`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data.showcases;
    } catch (error) {
      console.error('Failed to fetch showcase status:', error);
      throw error;
    }
  }

  // Admin methods

  /**
   * Get all AI models (admin only)
   */
  static async getAllAiModels(): Promise<AiModel[]> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/admin/onboarding/ai-models`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data.models;
    } catch (error) {
      console.error('Failed to fetch all AI models:', error);
      throw error;
    }
  }

  /**
   * Create a new AI model (admin only)
   */
  static async createAiModel(modelData: {
    name: string;
    gender: 'male' | 'female' | 'unisex';
    bodyType: string;
    imageUrl: string;
    thumbnailUrl?: string;
    description?: string;
    displayOrder?: number;
  }): Promise<AiModel> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/admin/onboarding/ai-models`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(modelData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to create AI model:', error);
      throw error;
    }
  }

  /**
   * Update an AI model (admin only)
   */
  static async updateAiModel(modelId: string, updates: Partial<AiModel>): Promise<void> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/admin/onboarding/ai-models/${modelId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updates),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to update AI model:', error);
      throw error;
    }
  }

  /**
   * Delete an AI model (admin only)
   */
  static async deleteAiModel(modelId: string): Promise<void> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/admin/onboarding/ai-models/${modelId}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to delete AI model:', error);
      throw error;
    }
  }

  /**
   * Upload AI model image (admin only)
   */
  static async uploadModelImage(image: string, modelId?: string): Promise<string> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/admin/onboarding/ai-models/upload`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ image, modelId }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data.imageUrl;
    } catch (error) {
      console.error('Failed to upload model image:', error);
      throw error;
    }
  }

  // Preview Step Methods

  /**
   * Start content generation for preview
   */
  static async generatePreview(
    brandId: string,
    productCode: string,
    selectedModels: string[],
    selectedProducts: string[]
  ): Promise<{ showcaseIds: string[]; totalItems: number }> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/onboarding/${brandId}/${productCode}/generate-preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ selectedModels, selectedProducts }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to generate preview:', error);
      throw error;
    }
  }

  /**
   * Get preview generation status
   */
  static async getPreviewStatus(brandId: string, productCode: string): Promise<PreviewStatus> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/onboarding/${brandId}/${productCode}/preview-status`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to fetch preview status:', error);
      throw error;
    }
  }

  /**
   * Regenerate a single preview item
   */
  static async regeneratePreviewItem(
    brandId: string,
    productCode: string,
    showcaseId: string
  ): Promise<void> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/onboarding/${brandId}/${productCode}/regenerate-preview/${showcaseId}`,
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to regenerate preview item:', error);
      throw error;
    }
  }

  // Video Review Methods

  /**
   * Get videos for review
   */
  static async getVideosForReview(brandId: string, productCode: string): Promise<{ videos: VideoForReview[]; stats: VideoReviewStats }> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/onboarding/${brandId}/${productCode}/videos`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to fetch videos for review:', error);
      throw error;
    }
  }

  /**
   * Approve a video
   */
  static async approveVideo(brandId: string, productCode: string, videoId: string): Promise<void> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/onboarding/${brandId}/${productCode}/videos/${videoId}/approve`,
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to approve video:', error);
      throw error;
    }
  }

  /**
   * Reject a video
   */
  static async rejectVideo(brandId: string, productCode: string, videoId: string, reason?: string): Promise<void> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/onboarding/${brandId}/${productCode}/videos/${videoId}/reject`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reason }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to reject video:', error);
      throw error;
    }
  }

  /**
   * Approve all pending videos
   */
  static async approveAllVideos(brandId: string, productCode: string): Promise<number> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/onboarding/${brandId}/${productCode}/videos/approve-all`,
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data.approvedCount;
    } catch (error) {
      console.error('Failed to approve all videos:', error);
      throw error;
    }
  }

  // Video Management Methods

  /**
   * Generate videos for multiple products with an AI model (bulk creation)
   */
  static async generateVideos(
    brandId: string,
    productCode: string,
    aiModelId: string,
    productIds: string[]
  ): Promise<{ showcaseIds: string[]; totalItems: number }> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/onboarding/${brandId}/${productCode}/generate-videos`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ aiModelId, productIds }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to generate videos:', error);
      throw error;
    }
  }

  /**
   * Toggle video active status
   */
  static async toggleVideoStatus(
    brandId: string,
    productCode: string,
    videoId: string
  ): Promise<{ id: string; isActive: boolean }> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/onboarding/${brandId}/${productCode}/videos/${videoId}/toggle`,
        {
          method: 'PATCH',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to toggle video status:', error);
      throw error;
    }
  }

  /**
   * Regenerate a single video
   */
  static async regenerateVideo(
    brandId: string,
    productCode: string,
    videoId: string
  ): Promise<void> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/onboarding/${brandId}/${productCode}/videos/${videoId}/regenerate`,
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to regenerate video:', error);
      throw error;
    }
  }

  // ===== Brand Custom Model Methods =====

  /**
   * Get all custom models for a brand
   */
  static async getBrandCustomModels(brandId: string): Promise<{
    models: BrandCustomModel[];
    count: number;
    limit: number;
  }> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/brands/${brandId}/custom-models`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to fetch brand custom models:', error);
      throw error;
    }
  }

  /**
   * Get custom model count for limit checking
   */
  static async getCustomModelCount(brandId: string): Promise<{
    count: number;
    limit: number;
    remaining: number;
    hasReachedLimit: boolean;
  }> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/brands/${brandId}/custom-models/count`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to fetch custom model count:', error);
      throw error;
    }
  }

  /**
   * Upload an image to create a custom model
   */
  static async uploadCustomModel(
    brandId: string,
    data: {
      name: string;
      gender: 'male' | 'female';
      image: string;
      bodyType?: string;
    }
  ): Promise<BrandCustomModel> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/brands/${brandId}/custom-models/upload`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Failed to upload custom model:', error);
      throw error;
    }
  }

  /**
   * Generate AI model preview
   */
  static async generateCustomModelPreview(
    brandId: string,
    config: {
      gender: 'male' | 'female';
      ageRange?: string;
      skinTone?: string;
      hairColor?: string;
      upperBody?: string;
      lowerBody?: string;
      bodyWeight?: string;
    }
  ): Promise<{
    previewId: string;
    imageUrl: string;
    promptConfig: {
      ageRange?: string;
      skinTone?: string;
      hairColor?: string;
      upperBody?: string;
      lowerBody?: string;
      bodyWeight?: string;
    };
  }> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/brands/${brandId}/custom-models/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(config),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Failed to generate custom model preview:', error);
      throw error;
    }
  }

  /**
   * Confirm and save AI-generated model
   */
  static async confirmCustomModel(
    brandId: string,
    data: {
      name: string;
      previewImageUrl: string;
      gender: 'male' | 'female';
      promptConfig?: {
        hairColor?: string;
        upperBody?: string;
        lowerBody?: string;
        bodyWeight?: string;
      };
      bodyType?: string;
    }
  ): Promise<BrandCustomModel> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/brands/${brandId}/custom-models/confirm`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Failed to confirm custom model:', error);
      throw error;
    }
  }

  /**
   * Delete a custom model
   */
  static async deleteCustomModel(brandId: string, modelId: string): Promise<void> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/brands/${brandId}/custom-models/${modelId}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to delete custom model:', error);
      throw error;
    }
  }

  /**
   * Toggle custom model active status
   */
  static async toggleCustomModelStatus(
    brandId: string,
    modelId: string
  ): Promise<{ id: string; isActive: boolean }> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/brands/${brandId}/custom-models/${modelId}/toggle`,
        {
          method: 'PATCH',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Failed to toggle custom model status:', error);
      throw error;
    }
  }
}

// Export BrandCustomModel type for use in components
export interface BrandCustomModel {
  id: string;
  name: string;
  gender: 'male' | 'female';
  bodyType?: string;
  imageUrl: string;
  thumbnailUrl?: string;
  creationType: 'upload' | 'ai_generated';
  isActive: boolean;
  createdAt: string;
}
